import { motion } from 'framer-motion';
import { useLongPress } from 'use-long-press';
import { isEmpty } from 'lodash';
import NewPhotoCard from './NewPhotoCard';
import { Virtuoso } from 'react-virtuoso';
import styled from '@emotion/styled';
import { EventNameBox } from '../../../components/event/EventNameBox';
import type { EventType, PhotoCardType } from '../../types/PhotoCardType';

const EventNameBoxWrapper = styled.div`
  overflow: hidden;
  padding-top: 20px;
  padding-bottom: 9px;
  padding-left: 24px;
  padding-right: 24px;
`;

type PhotoCardsByEventViewerProps = {
  events: EventType[];
  onLongClickedPhotoCard: (photoCard: PhotoCardType) => void;
  endReached: () => void;
  isNextPageLoading: boolean;
};

export default function PhotoCardsByEventViewer({
  events,
  onLongClickedPhotoCard,
  endReached,
  isNextPageLoading
}: PhotoCardsByEventViewerProps) {
  // #region draw cards
  const longPressHandler = useLongPress((_e, { context: photoCard }) => {
    onLongClickedPhotoCard(photoCard as PhotoCardType);
  });

  const drawCardsWithEventName = (e: EventType) => {
    if (isEmpty(e.photoCards)) {
      return null;
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: -1 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -1 }}
        transition={{ duration: 0.5 }}
      >
        <EventNameBoxWrapper>
          <EventNameBox event={e} />
        </EventNameBoxWrapper>

        <div className="px-[24px] grid grid-cols-[repeat(auto-fill,75px)] gap-[8px]">
          {e.photoCards?.map(
            (photoCard) =>
              photoCard && (
                <NewPhotoCard
                  key={photoCard.photoCardId}
                  photoCard={photoCard}
                  photoCardSize="sm"
                  {...longPressHandler(photoCard)}
                />
              )
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <Virtuoso
      useWindowScroll
      style={{ height: '100%' }}
      endReached={endReached}
      overscan={150}
      atBottomThreshold={100}
      data={events}
      itemContent={(_, e) => (
        <motion.div
          key={e.eventId}
          initial={{ opacity: 0, y: -1 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -1 }}
          transition={{ duration: 0.5 }}
        >
          {drawCardsWithEventName(e)}
        </motion.div>
      )}
      components={{
        Footer: () => isNextPageLoading && null
      }}
    />
  );
}

